import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MessageSquare,
  Send,
  Plus,
  Trash2,
  Bot,
  User,
  Loader2,
  Sparkles,
  Building,
  Target,
  ChevronLeft,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MarkdownRenderer({ content }) {
  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={`k1-${i}`} className="text-sm font-semibold text-foreground mt-3 mb-1">
            {renderInline(line.slice(4))}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`k2-${i}`} className="text-base font-semibold text-foreground mt-4 mb-1.5">
            {renderInline(line.slice(3))}
          </h2>
        );
      } else if (line.startsWith('# ')) {
        elements.push(
          <h1 key={`k3-${i}`} className="text-lg font-bold text-foreground mt-4 mb-2">
            {renderInline(line.slice(2))}
          </h1>
        );
      } else if (line.match(/^[-*]\s/)) {
        const listItems = [];
        while (i < lines.length && lines[i].match(/^[-*]\s/)) {
          listItems.push(
            <li key={`k4-${i}`} className="text-sm text-foreground/85 leading-relaxed">
              {renderInline(lines[i].replace(/^[-*]\s/, ''))}
            </li>
          );
          i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="list-disc list-inside space-y-0.5 my-1.5 pl-1">
            {listItems}
          </ul>
        );
        continue;
      } else if (line.match(/^\d+\.\s/)) {
        const listItems = [];
        while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
          listItems.push(
            <li key={`k5-${i}`} className="text-sm text-foreground/85 leading-relaxed">
              {renderInline(lines[i].replace(/^\d+\.\s/, ''))}
            </li>
          );
          i++;
        }
        elements.push(
          <ol key={`ol-${i}`} className="list-decimal list-inside space-y-0.5 my-1.5 pl-1">
            {listItems}
          </ol>
        );
        continue;
      } else if (line.trim() === '') {
        elements.push(<div key={`k6-${i}`} className="h-2" />);
      } else {
        elements.push(
          <p key={`k7-${i}`} className="text-sm text-foreground/85 leading-relaxed">
            {renderInline(line)}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`k1-${idx}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return <div className="space-y-0.5">{renderMarkdown(content)}</div>;
}

function SessionSidebar({ sessions, activeSession, onSelect, onNew, onDelete, isMobileOpen, onCloseMobile }) {
  return (
    <div className={`
      ${isMobileOpen ? 'fixed inset-0 z-50 bg-background/95 backdrop-blur-sm pt-4' : 'hidden'}
      lg:relative lg:flex lg:flex-col lg:w-72 lg:bg-card lg:border-r lg:border-border lg:inset-auto lg:z-auto
    `}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Conversations</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onNew}
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
          {isMobileOpen && (
            <Button variant="ghost" size="icon" className="h-8 w-8 lg:hidden" onClick={onCloseMobile}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 px-2 py-2">
        {sessions.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <Bot className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No conversations yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {sessions.map((s) => (
              <div
                key={s.session_id}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                  activeSession === s.session_id
                    ? 'bg-primary/10 border border-primary/30'
                    : 'hover:bg-secondary/60'
                }`}
                onClick={() => onSelect(s.session_id)}
                data-testid={`session-${s.session_id}`}
              >
                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm text-foreground truncate flex-1">
                  {s.title || 'New Chat'}
                </span>
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }}
                  data-testid={`delete-session-${s.session_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
          <Bot className="w-4 h-4 text-primary" />
        </div>
      )}
      <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
        isUser
          ? 'bg-primary text-primary-foreground'
          : 'bg-secondary/60 border border-border/50'
      }`}>
        {isUser ? (
          <p className="text-sm leading-relaxed">{msg.content}</p>
        ) : (
          <MarkdownRenderer content={msg.content} />
        )}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-lg bg-secondary border border-border flex items-center justify-center shrink-0 mt-0.5">
          <User className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

export default function Advisor() {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState('all');
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const canAccess = hasFeature('deal_advisor');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/advisor/sessions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/advisor/deals`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
      }
    } catch (err) {
      console.error('Failed to load deals:', err);
    }
  }, []);

  useEffect(() => {
    if (canAccess) {
      fetchSessions();
      fetchDeals();
    }
  }, [canAccess, fetchSessions, fetchDeals]);

  const loadSession = async (sessionId) => {
    setActiveSession(sessionId);
    setMobileSidebar(false);
    try {
      const res = await fetch(`${API_URL}/api/advisor/sessions/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        if (data.session?.deal_id) {
          setSelectedDeal(data.session.deal_id);
        }
      }
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  };

  const startNewChat = () => {
    setActiveSession(null);
    setMessages([]);
    setSelectedDeal('all');
    setMobileSidebar(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId) => {
    try {
      await fetch(`${API_URL}/api/advisor/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (activeSession === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      toast.success('Conversation deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const body = {
        message: text,
        session_id: activeSession || undefined,
        deal_id: selectedDeal !== 'all' ? selectedDeal : undefined,
      };

      const res = await fetch(`${API_URL}/api/advisor/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get response');
      }

      const data = await res.json();

      if (data.is_new_session) {
        setActiveSession(data.session_id);
        fetchSessions();
      }

      setMessages((prev) => [
        ...prev,
        {
          message_id: data.message.message_id,
          role: 'assistant',
          content: data.message.content,
          created_at: data.message.created_at,
        },
      ]);
    } catch (err) {
      toast.error(err.message);
      setMessages((prev) => prev.filter((m) => m.message_id !== userMsg.message_id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!canAccess) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4" data-testid="advisor-locked">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground">AI Deal Advisor</h2>
          <p className="text-muted-foreground text-center max-w-md text-sm">
            Get AI-powered insights about your deals, pipeline health, and strategic recommendations. Available on Pro and Team plans.
          </p>
          <Button onClick={() => navigate('/pricing')} data-testid="advisor-upgrade-btn">
            Upgrade to Pro
          </Button>
        </div>
      </Layout>
    );
  }

  const suggestedQuestions = [
    "Which deals need immediate attention?",
    "What's my pipeline health summary?",
    "Which deals have the highest risk?",
    "Compare my top 3 deals",
    "What objection patterns do you see?",
    "Which deals should I prioritize this week?",
  ];

  return (
    <Layout>
      <div className="flex h-[calc(100vh-8rem)] -m-4 lg:-m-8" data-testid="advisor-page">
        <SessionSidebar
          sessions={sessions}
          activeSession={activeSession}
          onSelect={loadSession}
          onNew={startNewChat}
          onDelete={deleteSession}
          isMobileOpen={mobileSidebar}
          onCloseMobile={() => setMobileSidebar(false)}
        />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b border-border bg-card/50">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 lg:hidden"
              onClick={() => setMobileSidebar(true)}
              data-testid="mobile-sessions-btn"
            >
              <MessageSquare className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Sparkles className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm font-semibold text-foreground truncate">Deal Advisor</span>
            </div>

            <Select value={selectedDeal} onValueChange={setSelectedDeal}>
              <SelectTrigger className="w-48 h-8 text-xs bg-background" data-testid="deal-selector">
                <SelectValue placeholder="All Deals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Deals</SelectItem>
                {deals.map((d) => (
                  <SelectItem key={d.call_id} value={d.call_id}>
                    <div className="flex items-center gap-2">
                      <span className="truncate">{d.title}</span>
                      {d.grade && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {d.score}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Messages area */}
          <ScrollArea className="flex-1 px-4 lg:px-6 py-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <Bot className="w-7 h-7 text-primary" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-foreground mb-1">AI Deal Advisor</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Ask me anything about your deals, pipeline health, or sales strategy.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                  {suggestedQuestions.map((q, idx) => (
                    <button
                      key={`k2-${idx}`}
                      className="text-left px-3 py-2.5 rounded-lg border border-border/60 bg-secondary/30 hover:bg-secondary/60 transition-colors text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      data-testid={`suggested-q-${idx}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>

                {deals.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Building className="w-3.5 h-3.5" />
                    <span>{deals.length} deals in your pipeline</span>
                    <span className="text-border">|</span>
                    <Target className="w-3.5 h-3.5" />
                    <span>
                      {deals.filter(d => d.grade === 'Hot Deal' || d.grade === 'SQL').length} qualified
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 pb-4">
                {messages.map((msg) => (
                  <ChatMessage key={msg.message_id} msg={msg} />
                ))}
                {sending && (
                  <div className="flex gap-3 justify-start" data-testid="advisor-typing">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-secondary/60 border border-border/50 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
                        <span className="text-xs text-muted-foreground">Analyzing your deals...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="px-4 lg:px-6 py-3 border-t border-border bg-card/50">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your deals..."
                rows={1}
                className="flex-1 resize-none rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 min-h-[40px] max-h-[120px]"
                style={{ height: 'auto', overflow: 'hidden' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                disabled={sending}
                data-testid="advisor-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || sending}
                size="icon"
                className="h-10 w-10 rounded-xl shrink-0"
                data-testid="advisor-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/50 text-center mt-1.5">
              AI advisor uses your pipeline data to provide contextual recommendations
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
